import React, { useState, useEffect } from "react";
import {
  Loader,
  Table,
  Dropdown,
  Button,
  Icon,
  Message,
  Popup,
} from "semantic-ui-react";
import { Fuelsform, Form } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { isAdmin, isMaster, isEditor, isReviewer } from "../../../../utils/checkPermission";
import "./ListFuelsForms.scss";
import { formatDateView } from "../../../../utils/formatDate";
import { FuelsForm } from "../FuelsForm";
import { PERIODS, normalizeUnit } from "../../../../utils";
import { BasicDrawer, ConfirmModal } from "../../../Shared";
import {
  convertUnitsFinal,
  fuelsCodes,
} from "../../../../utils/dataAndFormulas/fuelsForms";
import { useLanguage } from "../../../../contexts";
import { determineFuelsUnits } from "../../../../utils/dataAndFormulas/fuelsForms";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { UnitsForm } from "../UnitsForm";
import { UpdateUnitsModal } from "../UpdateUnitsModal";

const _ = require("lodash");

const fuelsFormController = new Fuelsform();
const formController = new Form();

export function ListFuelsForms(props) {
  const { reload, onReload, siteSelected, yearSelected, configFormData } =
    props;
  const [fuelsforms, setFuelsForms] = useState([]);
  const [loading, setLoading] = useState(false);

  const { user, accessToken } = useAuth();

  const { language, translations } = useLanguage();

  const t = (key) => translations[key] || key;

  useEffect(() => {
    (async () => {
      if (yearSelected !== "" && siteSelected !== undefined) {
        setLoading(true);
        try {
          const response = await fuelsFormController.getFuelsFormsBySiteAndYear(
            accessToken,
            siteSelected,
            yearSelected,
          );

          if (response.code === 200) {
            setFuelsForms(response.fuelsForms);
          } else {
            setFuelsForms([]);
          }
        } catch (error) {
          console.error(error);
          setFuelsForms([]);
        } finally {
          setLoading(false);
        }
      }
    })();
  }, [yearSelected, siteSelected, reload]);

  if (loading)
    return (
      <Loader
        active
        inline="centered"
        content="Cargando datos de combustibles..."
      />
    );

  return (
    <div className="list-fuels-forms">
      <TablePeriods
        data={fuelsforms}
        reload={reload}
        onReload={onReload}
        accessToken={accessToken}
        year={yearSelected}
        site={siteSelected}
        t={t}
        configFormData={configFormData}
        language={language}
        user={user}
      />
    </div>
  );
}

function TablePeriods(props) {
  const {
    data,
    reload,
    onReload,
    accessToken,
    year,
    site,
    t,
    configFormData,
    language,
    user,
  } = props;

  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState("");
  const [confirmContent, setConfirmContent] = useState("");
  const [dataDeleted, setDataDelete] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [uniqueFields, setUniqueFields] = useState([]);
  const [unitsRecorded, setUnitsRecorded] = useState([]);
  const [selectedUnits, setSelectedUnits] = useState([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);

  const [selectedPeriod, setSelectedPeriod] = useState(null);

  // Estados para edición múltiple
  const [editMode, setEditMode] = useState(false);
  const [editedValues, setEditedValues] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Estado para expandir filas
  const [expandedFields, setExpandedFields] = useState({});

  // Estado para el modal de configuración de unidades
  const [showUnitsModal, setShowUnitsModal] = useState(false);
  const [showUnitsWarning, setShowUnitsWarning] = useState(false);
  const [showMissingUnitsWarning, setShowMissingUnitsWarning] = useState(false);

  // Estado para el modal de modificar unidades en formularios existentes
  const [showUpdateUnitsModal, setShowUpdateUnitsModal] = useState(false);
  const [isUpdatingUnits, setIsUpdatingUnits] = useState(false);

  // Estado para el modal de advertencia de unidades
  const [showWarningModal, setShowWarningModal] = useState(false);

  // Estado para el modal de advertencia de cambio de unidades (l → L)
  const [showLegacyUnitWarning, setShowLegacyUnitWarning] = useState(false);
  const [pendingUnitsToSave, setPendingUnitsToSave] = useState(null);

  const onOpenCloseModal = () => setShowModal((prevState) => !prevState);
  const onOpenCloseConfirm = () => setShowConfirm((prevState) => !prevState);
  const onOpenCloseUnitsModal = () =>
    setShowUnitsModal((prevState) => !prevState);
  const onOpenCloseUpdateUnitsModal = () =>
    setShowUpdateUnitsModal((prevState) => !prevState);

  const toggleExpand = (fieldName) => {
    setExpandedFields((prev) => ({ ...prev, [fieldName]: !prev[fieldName] }));
  };

  const validateUnitsConfigured = () => {
    if (!configFormData?.fields || configFormData.fields.length === 0) {
      return { valid: true };
    }

    const activeFields = configFormData.fields.filter(
      (field) => field.active !== false,
    );

    if (activeFields.length === 0) {
      return { valid: false, message: t("no_active_fields") };
    }

    const fieldsWithUnit = activeFields.filter(
      (field) => field.unit && field.unit !== "",
    );

    if (fieldsWithUnit.length === 0) {
      return {
        valid: false,
        message:
          t("units_not_configured") ||
          "Debe configurar al menos una unidad antes de agregar un periodo. Haga clic en el ícono de tres puntos (⋯) en la columna de Unidades.",
      };
    }

    return { valid: true };
  };

  useEffect(() => {
    (async () => {
      try {
        if (site && year) {
          const fields = determineFields();
          setUniqueFields(fields);
        }
      } catch (error) {
        setUniqueFields([]);
      }
    })();
  }, [configFormData, reload]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasChanges && editMode) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasChanges, editMode]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (editMode) {
        if (e.ctrlKey && e.key === "s") {
          e.preventDefault();
          handleSaveChanges();
        } else if (e.key === "Escape") {
          handleCancelEdit();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [editMode, editedValues]);

  useEffect(() => {
    if (!uniqueFields?.length) return;

    const fetchUnits = async () => {
      try {
        let recordedUnits = {};

        if (configFormData?.fields && configFormData.fields.length > 0) {
          configFormData.fields.forEach((field) => {
            if (field.unit && field.name) {
              recordedUnits[field.name] = field.unit;
            }
          });
        }

        setUnitsRecorded(recordedUnits);
        setShowUnitsWarning(
          !configFormData && Object.keys(recordedUnits).length > 0,
        );

        const values = {};
        uniqueFields.forEach((field) => {
          values[field] = recordedUnits[field] || "";
        });

        setSelectedUnits(values);

        if (configFormData?.fields && configFormData.fields.length > 0) {
          const activeFieldsWithoutUnit = configFormData.fields.filter(
            (field) => field.active && !field.unit,
          );
          setShowMissingUnitsWarning(activeFieldsWithoutUnit.length > 0);
        } else {
          setShowMissingUnitsWarning(false);
        }
      } catch (error) {
        console.error(error);
        setUnitsRecorded({});
        const values = {};
        uniqueFields.forEach((field) => {
          values[field] = "";
        });
        setSelectedUnits(values);
      }
    };

    fetchUnits();
  }, [uniqueFields, configFormData, reload]);

  const handleDeleteModal = (period) => {
    const form = data.find((item) => item.period === period);
    setConfirmContent(
      `${t("delete_dated_fuels_form")} ${formatDateView(form.date)}`,
    );
    setDataDelete(form);
    onOpenCloseConfirm();
  };

  const openUpdateFuelsForm = (period) => {
    const form = data.find((item) => item.period === period);
    setModalContent(
      <FuelsForm
        onClose={() => setShowModal(false)}
        onReload={onReload}
        fuelsForm={form}
        siteSelected={site}
        configFormData={configFormData}
        unitsRecorded={unitsRecorded}
      />,
    );
    setShowModal(true);
  };

  const openNewFuelsForm = (period) => {
    const validation = validateUnitsConfigured();
    if (!validation.valid) {
      toast.error(validation.message);
      return;
    }

    setModalContent(
      <FuelsForm
        onClose={() => setShowModal(false)}
        onReload={onReload}
        period={period}
        year={year}
        siteSelected={site}
        configFormData={configFormData}
        unitsRecorded={unitsRecorded}
      />,
    );
    setShowModal(true);
  };

  const handleUnitsFormSave = (units) => {
    const hasExistingUnits = configFormData?.fields?.some(f => f.unit);
    if (hasExistingUnits) {
      setPendingUnitsToSave(units);
      setShowLegacyUnitWarning(true);
    } else {
      handleSaveUnits(units);
    }
  };

  const handleSaveUnits = async (units) => {
    try {
      const formId = configFormData?._id;
      const currentFields = configFormData?.fields || [];

      if (!formId || currentFields.length === 0) {
        const allFuelsFieldsData = [
          { name: "natural_gas", code: "Veh1" },
          { name: "natural_gas_cost", code: "Veh2" },
          { name: "glp", code: "Veh3" },
          { name: "glp_cost", code: "Veh4" },
          { name: "diesel", code: "Veh5" },
          { name: "diesel_cost", code: "Veh6" },
          { name: "biodiesel", code: "Veh7" },
          { name: "cost_biodiesel", code: "Veh8" },
          { name: "gasoline", code: "Veh9" },
          { name: "gasoline_cost", code: "Veh10" },
          { name: "ethanol", code: "Veh11" },
          { name: "ethanol_cost", code: "Veh12" },
          { name: "electricity_vehicles", code: "Veh13" },
          { name: "cost_electricity_vehicles", code: "Veh14" },
          { name: "natural_gas_vehicles", code: "Veh15" },
          { name: "glp_vehicles", code: "Veh16" },
          { name: "diesel_vehicles", code: "Veh17" },
          { name: "biodiesel_vehicles", code: "Veh18" },
          { name: "gasoline_vehicles", code: "Veh19" },
          { name: "ethanol_vehicles", code: "Veh20" },
          { name: "electric_vehicles", code: "Veh21" },
        ];

        const fieldsToCreate = allFuelsFieldsData.map((field) => ({
          code: field.code,
          name: field.name,
          active: true,
          unit: units[field.name] || "",
          standardized_coefficient: 1,
        }));

        const newFormData = {
          module: "fuels",
          site: site,
          fields: fieldsToCreate,
        };

        const response = await formController.createForm(
          accessToken,
          newFormData,
        );

        if (response.code === 200 || response._id) {
          toast.success(
            t("units_updated_successfully") ||
              "Unidades actualizadas correctamente",
          );
          setShowUnitsWarning(false);
          onOpenCloseUnitsModal();
          onReload();
        } else {
          toast.error(
            t("error_updating_units") || "Error al actualizar las unidades",
          );
        }
      } else {
        // Solo se envían los campos que cambian de unidad, de forma atómica
        // (ver updateFormFields en el controlador) — no se pisa el
        // documento entero, para no perder un toggle de 'active' guardado
        // desde Fuentes casi al mismo tiempo.
        const changes = currentFields
          .filter((field) => Object.prototype.hasOwnProperty.call(units, field.name))
          .map((field) => ({ name: field.name, unit: units[field.name] }));

        const response =
          changes.length > 0
            ? await formController.updateFormFields(accessToken, formId, changes)
            : { code: 200 };

        if (response.code === 200) {
          toast.success(
            t("units_updated_successfully") ||
              "Unidades actualizadas correctamente",
          );
          setShowUnitsWarning(false);
          onOpenCloseUnitsModal();
          onReload();
        } else {
          toast.error(
            t("error_updating_units") || "Error al actualizar las unidades",
          );
        }
      }
    } catch (error) {
      console.error("Error al guardar unidades:", error);
      toast.error(
        t("error_updating_units") || "Error al actualizar las unidades",
      );
    }
  };

  const handleApplyUnitsToForms = async (unitsToApply) => {
    if (!unitsToApply || Object.keys(unitsToApply).length === 0) {
      toast.warning(t("no_fields_selected") || "No hay campos seleccionados");
      return;
    }

    setIsUpdatingUnits(true);
    try {
      const response = await fuelsFormController.updateUnitsForYear(
        accessToken,
        site,
        year,
        unitsToApply,
      );

      if (response.code === 200) {
        toast.success(
          `${t("units_applied_to_forms") || "Unidades aplicadas a"} ${response.updated} ${t("forms") || "formularios"}`,
        );
        onOpenCloseUpdateUnitsModal();
        onReload();
      } else {
        toast.error(
          t("error_applying_units_to_forms") || "Error al aplicar unidades",
        );
      }
    } catch (error) {
      console.error("Error applying units:", error);
      toast.error(
        t("error_applying_units_to_forms") || "Error al aplicar unidades",
      );
    } finally {
      setIsUpdatingUnits(false);
    }
  };

  const onDelete = async () => {
    try {
      const response = await fuelsFormController.deleteFuelsForm(
        accessToken,
        dataDeleted._id,
      );
      if (response.code === 200) {
        setIsSuccess(true);
        setModalMessage("Formulario eliminado correctamente!");
      } else {
        setIsSuccess(false);
        setModalMessage("Error al eliminar el formulario.");
      }

      setModalOpen(true);
      setDataDelete("");
      onOpenCloseConfirm();
      onReload();
    } catch (error) {
      console.error(error);
      setIsSuccess(false);
      setModalMessage("Error al eliminar el formulario.");
      setModalOpen(true);
    }
  };

  const periods = PERIODS;

  const determineFields = () => {
    const fields = new Set();
    if (
      configFormData &&
      configFormData.fields &&
      configFormData.fields.length > 0
    ) {
      Object.keys(fuelsCodes).forEach((key) => {
        if (
          configFormData.fields &&
          configFormData.fields.some(
            (field) => field.name === key && field.active,
          )
        ) {
          fields.add(key);
        }
      });
    } else {
      Object.keys(fuelsCodes).forEach((key) => {
        fields.add(key);
      });
    }
    return Array.from(fields);
  };

  const hasDataPeriod = (period) => {
    return data.some((entry) => entry.period === period);
  };

  function formatNumberWithThousands(numero) {
    if (typeof numero !== "number" || isNaN(numero)) {
      return numero;
    }
    const entero = Math.round(numero);
    return entero.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  }

  const calculateTotalAndAverage = (field) => {
    const values = data.map((item) => item[field]?.value || 0);
    const totalFinal = values.reduce((ac, value) => ac + value, 0);
    const total = formatNumberWithThousands(totalFinal);
    const averageFinal = values.length ? totalFinal / values.length : 0;
    const average = formatNumberWithThousands(averageFinal);
    return { total, average };
  };

  const getUnitName = (field) => {
    const unitValue = normalizeUnit(selectedUnits[field]);
    if (!unitValue || unitValue === "") {
      return "- - -";
    }
    const unitsArray = determineFuelsUnits(field);
    const unitObject = unitsArray.find((u) => u.value === unitValue);
    return unitObject?.name || unitValue;
  };

  const handleCellChange = (field, period, item, newValue) => {
    const key = `${period}_${field}`;
    setEditedValues((prev) => ({
      ...prev,
      [key]: {
        formId: item?._id || null,
        period: period,
        field,
        value: newValue,
        unit: item?.[field]?.unit || selectedUnits[field],
        isNew: !item || !item._id,
        year: year,
        site: site,
      },
    }));
    setHasChanges(true);
  };

  const handleSaveChanges = async () => {
    if (!hasChanges || Object.keys(editedValues).length === 0) {
      toast.info("No hay cambios para guardar");
      return;
    }

    setIsSaving(true);

    try {
      const updates = [];
      const creates = [];

      Object.values(editedValues).forEach((change) => {
        if (change.isNew) {
          creates.push({
            period: change.period,
            year: change.year,
            site: change.site,
            field: change.field,
            value: change.value,
            unit: change.unit,
            code: fuelsCodes[change.field],
          });
        } else {
          updates.push({
            formId: change.formId,
            field: change.field,
            value: change.value,
            unit: change.unit,
            code: fuelsCodes[change.field],
          });
        }
      });

      const response = await fuelsFormController.batchUpdateFuelsForms(
        accessToken,
        {
          updates,
          creates,
          site,
          year,
          creatorUser: user._id,
        },
      );

      if (response.code === 200) {
        toast.success(
          `✅ ${response.updated || 0} formularios actualizados, ${response.created || 0} creados`,
        );
        setEditedValues({});
        setHasChanges(false);
        setEditMode(false);
        onReload();
      } else {
        toast.error("❌ Error al guardar los cambios");
      }
    } catch (error) {
      console.error("Error saving batch changes:", error);
      toast.error("❌ Error al guardar los cambios");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    if (hasChanges) {
      const confirmed = window.confirm(
        "Hay cambios sin guardar. ¿Deseas cancelar la edición?",
      );
      if (!confirmed) return;
    }
    setEditMode(false);
    setEditedValues({});
    setHasChanges(false);
  };

  const toggleEditMode = () => {
    if (editMode && hasChanges) {
      const confirmed = window.confirm(
        "Hay cambios sin guardar. ¿Deseas salir del modo edición?",
      );
      if (!confirmed) return;
    }
    setEditMode(!editMode);
    setEditedValues({});
    setHasChanges(false);
  };

  return (
    <>
      {/* Botones de control de edición múltiple */}
      {(isMaster(user.role) || isAdmin(user.role) || isEditor(user.role)) && (
      <div className="batch-actions">
        {!editMode ? (
          <Popup
            content={t("enable_multiple_edit")}
            trigger={
              <Button
                className="batch-btn primary"
                icon
                //labelPosition="left"
                onClick={toggleEditMode}
                disabled={data.length === 0}
              >
                <Icon name="edit" />
                {/* {t("enable_multiple_edit") || "Habilitar edición múltiple"} */}
              </Button>
            }
          />
        ) : (
          <>
            <Button
              className="batch-btn success"
              icon
              labelPosition="left"
              onClick={handleSaveChanges}
              disabled={!hasChanges || isSaving}
              loading={isSaving}
            >
              <Icon name="save" />
              {t("save_changes") || "Guardar cambios"}
            </Button>
            <Button
              className="batch-btn cancel"
              icon
              labelPosition="left"
              onClick={handleCancelEdit}
              disabled={isSaving}
            >
              <Icon name="cancel" />
              {t("cancel") || "Cancelar"}
            </Button>
            {hasChanges && (
              <span style={{ color: "#f57c00", fontWeight: "bold" }}>
                <Icon name="warning sign" />
                {Object.keys(editedValues).length} cambio(s) pendiente(s)
              </span>
            )}
          </>
        )}
      </div>
      )}

      {hasChanges && (
        <Message warning>
          <Message.Header>Cambios sin guardar</Message.Header>
          <p>
            Tienes {Object.keys(editedValues).length} cambio(s) pendiente(s). No
            olvides guardar antes de salir. (Ctrl+S para guardar, Esc para
            cancelar)
          </p>
        </Message>
      )}

      {showUnitsWarning && (
        <Message info>
          <Message.Header>Configuración de unidades recomendada</Message.Header>
          <p>
            Las unidades mostradas provienen de formularios guardados. Para una
            mejor consistencia, se recomienda configurar las unidades
            explícitamente haciendo clic en el ícono de tres puntos (⋯) en la
            columna "Unidades".
          </p>
        </Message>
      )}

      <div className="list-fuels-forms__table-wrapper">
        <Table className="fuels-table" unstackable>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell className="sticky-col-1 pill-left-start">
                {t("code")}
              </Table.HeaderCell>
              <Table.HeaderCell className="sticky-col-2">
                {t("concept")}
              </Table.HeaderCell>
              <Table.HeaderCell className="pill-left-end">
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "6px",
                  }}
                >
                  {t("units")}
                  {showMissingUnitsWarning && (
                    <Icon
                      name="warning sign"
                      color="orange"
                      style={{ cursor: "pointer", margin: 0 }}
                      onClick={() => setShowWarningModal(true)}
                    />
                  )}
                  <Dropdown
                    icon="ellipsis vertical"
                    floating
                    className="icon"
                    style={{ cursor: "pointer" }}
                  >
                    <Dropdown.Menu>
                      <Dropdown.Item
                        text={t("configure_units") || "Configurar unidades"}
                        icon="setting"
                        onClick={onOpenCloseUnitsModal}
                      />
                      {data.length > 0 && (
                        <Dropdown.Item
                          text={
                            t("update_units_in_forms") ||
                            "Modificar unidades en formularios"
                          }
                          icon="edit"
                          onClick={onOpenCloseUpdateUnitsModal}
                        />
                      )}
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
              </Table.HeaderCell>
              <Table.HeaderCell className="pill-gap" />
              <Table.HeaderCell
                colSpan="3"
                textAlign="center"
                className="pill-right"
              >
                {t("report_period")} {year}
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {uniqueFields.map((field) => {
              const isExpanded = expandedFields[field];
              const { total, average } = calculateTotalAndAverage(field);
              const hasMismatchedUnit = data.some((item) => {
                const itemUnit = normalizeUnit(item?.[field]?.unit);
                const configuredUnit = normalizeUnit(selectedUnits[field]);
                return itemUnit && configuredUnit && itemUnit !== configuredUnit;
              });

              return (
                <React.Fragment key={field}>
                  {/* Fila resumen */}
                  <Table.Row>
                    <Table.Cell className="sticky-col-1 pill-left-start">
                      {fuelsCodes[field]}
                    </Table.Cell>
                    <Table.Cell className="sticky-col-2">{t(field)}</Table.Cell>
                    <Table.Cell textAlign="center" className="pill-left-end">
                      {getUnitName(field)}
                      {hasMismatchedUnit && (
                        <Popup
                          content="Hay meses con unidades diferentes a la configurada"
                          trigger={
                            <Icon
                              name="warning sign"
                              color="orange"
                              size="small"
                              style={{ cursor: "pointer", marginLeft: "4px" }}
                            />
                          }
                        />
                      )}
                    </Table.Cell>
                    <Table.Cell className="pill-gap" />
                    <Table.Cell colSpan="3" className="pill-right">
                      <div className="summary-flex">
                        <span className="summary-item">
                          {t("total")}: <strong>{total}</strong>
                        </span>
                        <span className="summary-item">
                          {t("average")}: <strong>{average}</strong>
                        </span>
                        <Button
                          icon
                          size="tiny"
                          className="expand-btn"
                          onClick={() => toggleExpand(field)}
                        >
                          <Icon
                            name={isExpanded ? "chevron up" : "chevron down"}
                          />
                        </Button>
                      </div>
                    </Table.Cell>
                  </Table.Row>

                  {/* Fila cabecera de meses (expandida) */}
                  {isExpanded && (
                    <Table.Row className="months-header-row">
                      <Table.Cell
                        colSpan="7"
                        style={{ padding: 0 }}
                        className="months-full-row"
                      >
                        <div className="months-flex-container">
                          {periods.map((period) => (
                            <div
                              key={period}
                              className="month-flex-cell month-flex-header"
                            >
                              {t(period).slice(0, 3)}
                              {!editMode &&
                                (hasDataPeriod(period) ? (
                                  <Dropdown
                                    inline
                                    size="tiny"
                                    icon="ellipsis vertical"
                                    floating
                                    className="icon"
                                  >
                                    <Dropdown.Menu>
                                      <Dropdown.Item
                                        text={t("edit")}
                                        icon={
                                          <Icon
                                            name="edit"
                                            style={{ color: "gray" }}
                                          />
                                        }
                                        onClick={() =>
                                          openUpdateFuelsForm(period)
                                        }
                                      />
                                      <Dropdown.Item
                                        text={t("delete")}
                                        icon={
                                          <Icon
                                            name="trash"
                                            style={{ color: "red" }}
                                          />
                                        }
                                        onClick={() =>
                                          handleDeleteModal(period)
                                        }
                                      />
                                    </Dropdown.Menu>
                                  </Dropdown>
                                ) : (
                                  (isAdmin(user.role) ||
                                    isEditor(user.role) ||
                                    isMaster(user.role)) && (
                                    <Dropdown
                                      link
                                      size="tiny"
                                      icon="ellipsis vertical"
                                      floating
                                      className="icon"
                                    >
                                      <Dropdown.Menu>
                                        <Dropdown.Item
                                          text={t("load_data")}
                                          icon="plus"
                                          onClick={() =>
                                            openNewFuelsForm(period)
                                          }
                                        />
                                      </Dropdown.Menu>
                                    </Dropdown>
                                  )
                                ))}
                            </div>
                          ))}
                        </div>
                      </Table.Cell>
                    </Table.Row>
                  )}

                  {/* Fila datos de meses (expandida) */}
                  {isExpanded && (
                    <Table.Row className="months-data-row">
                      <Table.Cell
                        colSpan="7"
                        style={{ padding: 0 }}
                        className="months-full-row"
                      >
                        <div className="months-flex-container">
                          {periods.map((period) => {
                            const item = data.find((d) => d.period === period);
                            const editKey = `${period}_${field}`;
                            const isEdited =
                              editedValues[editKey] !== undefined;
                            const hasUnit =
                              selectedUnits[field] &&
                              selectedUnits[field] !== "";
                            const itemUnit = normalizeUnit(item?.[field]?.unit);
                            const configuredUnit = normalizeUnit(selectedUnits[field]);
                            const isDifferentUnit =
                              itemUnit &&
                              configuredUnit &&
                              itemUnit !== configuredUnit;
                            let cellContent;

                            if (editMode) {
                              const displayVal = isEdited
                                ? editedValues[editKey].value
                                : (item?.[field]?.value ?? "");
                              cellContent = (
                                <input
                                  type="number"
                                  step="0.01"
                                  value={displayVal}
                                  onChange={(e) =>
                                    handleCellChange(
                                      field,
                                      period,
                                      item,
                                      parseFloat(e.target.value) || 0,
                                    )
                                  }
                                  disabled={!hasUnit || isReviewer(user.role)}
                                  style={{
                                    width: "100%",
                                    height: "30px",
                                    border: isEdited
                                      ? "2px solid #fbc02d"
                                      : "1px solid #ccc",
                                    borderRadius: "4px",
                                    padding: "2px 6px",
                                    textAlign: "right",
                                    fontSize: "11px",
                                    background: hasUnit ? "white" : "#f5f5f5",
                                    color: !hasUnit ? "#999" : isReviewer(user.role) ? "#000" : "inherit",
                                  }}
                                  placeholder="-"
                                />
                              );
                            } else {
                              cellContent = item?.[field] ? (
                                <span>
                                  {formatNumberWithThousands(item[field].value)}
                                  {isDifferentUnit && (
                                    <span
                                      style={{
                                        fontSize: "0.75em",
                                        color: "#f57c00",
                                        marginLeft: "2px",
                                      }}
                                      title={`Unidad: ${itemUnit} | Configurada: ${configuredUnit}`}
                                    >
                                      ({itemUnit})
                                    </span>
                                  )}
                                </span>
                              ) : (
                                "-"
                              );
                            }

                            return (
                              <div
                                key={period}
                                className="month-flex-cell month-flex-data"
                              >
                                {cellContent}
                              </div>
                            );
                          })}
                        </div>
                      </Table.Cell>
                    </Table.Row>
                  )}
                </React.Fragment>
              );
            })}
          </Table.Body>
        </Table>
      </div>

      <ToastContainer
        position="top-right"
        autoClose={1200}
        hideProgressBar
        newestOnTop={false}
        closeOnClick={false}
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />

      <BasicDrawer show={showModal} close={() => setShowModal(false)}>
        {modalContent}
      </BasicDrawer>

      <ConfirmModal
        open={showConfirm}
        title={t("delete")}
        content={confirmContent}
        onConfirm={onDelete}
        onCancel={onOpenCloseConfirm}
      />

      <ConfirmModal
        open={modalOpen}
        icon={<Icon name={isSuccess ? "check circle outline" : "warning sign"} color={isSuccess ? "green" : "red"} />}
        title={isSuccess ? "Éxito" : "Error"}
        content={modalMessage}
        onCancel={() => setModalOpen(false)}
        cancelText="Cerrar"
      />

      <UnitsForm
        show={showUnitsModal}
        onClose={onOpenCloseUnitsModal}
        onSave={handleUnitsFormSave}
        currentUnits={unitsRecorded}
        registeredFields={configFormData?.fields || []}
      />

      <UpdateUnitsModal
        show={showUpdateUnitsModal}
        onClose={onOpenCloseUpdateUnitsModal}
        onApply={handleApplyUnitsToForms}
        currentUnits={unitsRecorded}
        registeredFields={configFormData?.fields || []}
        isLoading={isUpdatingUnits}
      />

      <ConfirmModal
        open={showWarningModal}
        icon={<Icon name="warning sign" color="orange" />}
        title="Unidades sin configurar"
        content={
          <>
            <p>Los campos no tienen unidades asignadas en la configuración.</p>
            <p>
              Por favor configure las unidades haciendo clic en el ícono{" "}
              <strong>⋯</strong> en la columna Unidades.
            </p>
          </>
        }
        onCancel={() => setShowWarningModal(false)}
        onConfirm={() => {
          setShowWarningModal(false);
          onOpenCloseUnitsModal();
        }}
        cancelText="Cerrar"
        confirmText="Configurar unidades"
      />

      <ConfirmModal
        open={showLegacyUnitWarning}
        icon={<Icon name="warning sign" color="orange" />}
        title="Cambio de unidades"
        content={
          <>
            <p>
              Los registros anteriores continuarán guardados con la unidad{" "}
              <strong>"l"</strong> (litros). A partir de ahora, los nuevos
              datos se guardarán con <strong>"L"</strong> (Litros).
            </p>
            <p>¿Desea continuar con el cambio?</p>
          </>
        }
        onCancel={() => setShowLegacyUnitWarning(false)}
        onConfirm={() => {
          setShowLegacyUnitWarning(false);
          handleSaveUnits(pendingUnitsToSave);
        }}
        cancelText="Cancelar"
        confirmText="Confirmar"
      />
    </>
  );
}
